import { DateTime } from 'luxon';
import { SampleTagCreateData } from '../../../../Lib/Api/sample-tags';
import {
  AddTagFormData,
  FabricSection,
  ItemFilters,
  SampleTagResponse,
  DevelopmentType,
  MaterialSection,
  SampleCreatedWith,
} from '../types';
import { SAMPLE_TYPE_CONFIG } from '../constants';

export const calculateLeadTimeDate = (
  leadTimeWeeks: number | null | undefined,
): string | undefined => {
  if (leadTimeWeeks == null || leadTimeWeeks < 0) {
    return undefined;
  }

  const leadTimeDate = DateTime.utc().plus({ weeks: leadTimeWeeks });

  if (!leadTimeDate.isValid) {
    return undefined;
  }

  return leadTimeDate.toISODate() || undefined;
};

/**
 * Transforms form data to API format
 */
export const transformFormDataToAPI = (
  formData: AddTagFormData,
): SampleTagCreateData => {
  const sampleTypeMap = {
    [ItemFilters.FIRST_SAMPLE]: 'First Sample',
    [ItemFilters.PROTO_DEVELOPMENT]: 'Proto Development',
    [ItemFilters.FIT]: 'Fit',
    [ItemFilters.PRE_PRODUCTION]: 'Pre-Production',
    [ItemFilters.TOP]: 'TOP',
    [ItemFilters.BLOCK]: 'Block',
  } as const;

  const transformFabricSection = (section: FabricSection | undefined) => {
    if (!section) return undefined;

    return {
      content: section.fiberContents.map((fiber) => ({
        fiber: fiber.fiber,
        percent_of_fibers: fiber.percent_of_fibers,
      })),
      fabric_weight: section.fabricWeight
        ? parseFloat(section.fabricWeight)
        : undefined,
      fabric_code_name: section.fabricCodeName || undefined,
    };
  };

  const transformSampleCreatedWith = (): Partial<SampleCreatedWith> => {
    // Block samples only use fabric field
    if (formData.sampleType === ItemFilters.BLOCK) {
      return {
        ...(formData.fabricType && { fabric: formData.fabricType }),
      };
    }

    // Other sample types use all fields
    return {
      ...(formData.fabricType && { fabric: formData.fabricType }),
      ...(formData.trimType && { trim: formData.trimType }),
      ...(formData.labelType && { label: formData.labelType }),
      ...(formData.washColorType && { wash_color: formData.washColorType }),
    };
  };

  const transformSelfSection = () => {
    if (!formData.fiberContents || formData.fiberContents.length === 0) {
      return undefined;
    }

    return {
      content: formData.fiberContents.map((fiber) => ({
        fiber: fiber.fiber,
        percent_of_fibers: fiber.percent_of_fibers,
      })),
      fabric_weight: formData.fabricWeight
        ? parseFloat(formData.fabricWeight)
        : undefined,
      fabric_code_name: formData.fabricCodeName || undefined,
    };
  };

  const hasSampleCreatedWithFields =
    formData.sampleType === ItemFilters.BLOCK
      ? formData.fabricType !== undefined
      : formData.fabricType !== undefined ||
        formData.trimType !== undefined ||
        formData.labelType !== undefined ||
        formData.washColorType !== undefined;

  const apiData: SampleTagCreateData = {
    type_of_sample_tag: sampleTypeMap[formData.sampleType],
  };

  const { sampleType } = formData;
  const configSections = SAMPLE_TYPE_CONFIG[sampleType]?.sections || [];

  if (configSections.includes('productImage')) {
    if (formData.sampleType !== ItemFilters.BLOCK) {
      apiData.style_number = formData.styleNumber;
    }
    apiData.product_image = formData.productImageUrl;
  }

  if (configSections.includes('productClassification')) {
    if (formData.division) {
      apiData.division = formData.division;
    }
    if (formData.department) {
      apiData.department = formData.department;
    }
    if (formData.category) {
      apiData.category = formData.category;
    }
    if (formData.class) {
      apiData.class = formData.class;
    }
  }

  if (configSections.includes('poAndProduct')) {
    apiData.po_number = formData.poNumber?.toUpperCase();
    apiData.product_sku = formData.selectedProduct;
    apiData.product_image = formData.productImageUrl;
    apiData.po_due_date = formData.poDueDate;
    apiData.po_line_name = formData.poLineName;
    apiData.po_line_color = formData.poLineColor;
    apiData.po_line_size = formData.poLineSize;
  }

  if (configSections.includes('dateFields')) {
    if (formData.dateSent) {
      apiData.date_sent = formData.dateSent;
    }
    if (formData.cutDate) {
      apiData.cut_date = formData.cutDate;
    }
  }

  if (configSections.includes('protoFields')) {
    apiData.type_of_development = formData.typeOfDevelopment;
    apiData.sample_size = formData.sampleSize;
    apiData.color_wash = formData.colorWash;
    apiData.style_number = formData.styleNumber;
    apiData.product_image = formData.productImageUrl;
    apiData.style_name = formData.styleName || null;
    apiData.custom_cut_lead_time = formData.customCutLeadTime || null;
    apiData.cc_lead_time_date =
      calculateLeadTimeDate(formData.customCutLeadTime) || null;
    apiData.coo = formData.coo || null;
    apiData.shipment_mode = formData.shipmentMode || null;
    apiData.division = formData.division || null;
    apiData.department = formData.department || null;
    apiData.category = formData.category || null;
    apiData.class = formData.class || null;
  }

  if (configSections.includes('logistics')) {
    if (formData.customCutLeadTime != null) {
      apiData.custom_cut_lead_time = formData.customCutLeadTime;
      apiData.cc_lead_time_date = calculateLeadTimeDate(
        formData.customCutLeadTime,
      );
    }
    if (formData.coo) {
      apiData.coo = formData.coo;
    }
    if (formData.shipmentMode) {
      apiData.shipment_mode = formData.shipmentMode;
    }
  }

  if (configSections.includes('sampleDetails')) {
    if (formData.fnExclusive !== undefined) {
      apiData.fn_exclusive = formData.fnExclusive;
    }
    if (formData.fitSample !== undefined) {
      apiData.fit_sample = formData.fitSample;
    }
  }

  if (configSections.includes('costAndMoq')) {
    if (formData.juniorCost !== undefined) {
      apiData.junior_cost = formData.juniorCost;
    }
    apiData.junior_moq = formData.juniorMoq || null;
    if (formData.plusCost !== undefined) {
      apiData.plus_cost = formData.plusCost;
    }
    apiData.plus_moq = formData.plusMoq || null;
  }

  if (hasSampleCreatedWithFields) {
    apiData.sample_created_with = transformSampleCreatedWith();
  }

  if (configSections.includes('selfShell')) {
    const selfSection = transformSelfSection();
    if (selfSection) {
      apiData.self = selfSection;
    }
  }

  if (configSections.includes('additionalFabrics')) {
    const contrastSection = transformFabricSection(formData.contrastSection);
    if (contrastSection) {
      apiData.contrast = contrastSection;
    }

    const liningSection = transformFabricSection(formData.liningSection);
    if (liningSection) {
      apiData.lining = liningSection;
    }

    const fillingSection = transformFabricSection(formData.fillingSection);
    if (fillingSection) {
      apiData.filling = fillingSection;
    }

    const coatingSection = transformFabricSection(formData.coatingSection);
    if (coatingSection) {
      apiData.coating = coatingSection;
    }

    const backingSection = transformFabricSection(formData.backingSection);
    if (backingSection) {
      apiData.backing = backingSection;
    }
  }

  if (configSections.includes('blockHeader')) {
    if (formData.blockNumber) {
      apiData.block_number = formData.blockNumber;
    }
    if (formData.category) {
      apiData.category = formData.category;
    }
  }

  if (configSections.includes('blockSampleDetails')) {
    if (formData.sampleSize) {
      apiData.sample_size = formData.sampleSize;
    }
    if (formData.selfShellFabricCode) {
      apiData.self_shell_fabric_code = formData.selfShellFabricCode;
    }
  }

  if (configSections.includes('blockOtherAttributes')) {
    if (formData.pantsAttribute) {
      apiData.pants_attribute = formData.pantsAttribute;
    }
    if (formData.topsAttribute) {
      apiData.tops_attribute = formData.topsAttribute;
    }
  }

  if (configSections.includes('comments')) {
    if (formData.comments) {
      apiData.comments = formData.comments;
    }
  }
  if (formData.productImageUrl && !apiData.product_image) {
    apiData.product_image = formData.productImageUrl;
  }
  if (
    formData.styleNumber &&
    !apiData.style_number &&
    formData.sampleType !== ItemFilters.BLOCK
  ) {
    apiData.style_number = formData.styleNumber;
  }

  return apiData;
};
const transformMaterialToFabricSection = (
  materialSection?: MaterialSection,
  sectionType?: string,
): FabricSection | undefined => {
  if (!materialSection) return undefined;

  return {
    id: sectionType || materialSection.fabric_code_name || 'fabric-section',
    fiberContents: materialSection.content.map(
      (
        item: {
          fiber: string;
          percent_of_fibers: number;
        },
        index: number,
      ) => ({
        id: `fiber-${index}`,
        fiber: item.fiber,
        percent_of_fibers: item.percent_of_fibers,
      }),
    ),
    fabricWeight: materialSection.fabric_weight?.toString() || '',
    fabricCodeName: materialSection.fabric_code_name || '',
  };
};

export const transformApiResponseToFormData = (
  response: SampleTagResponse,
): AddTagFormData => ({
  sampleType:
    Object.values(ItemFilters).find(
      (filter) => filter === response.type_of_sample_tag,
    ) || ItemFilters.FIRST_SAMPLE,

  styleNumber: response.style_number || '',
  styleName: response.style_name || '',
  productImageUrl: response.product_image || '',
  division: response.division || '',
  department: response.department || '',
  category: response.category || '',
  class: response.class || '',
  dateSent: response.date_sent || '',
  cutDate: response.cut_date || '',
  poDueDate: response.po_due_date || '',
  customCutLeadTime: response.custom_cut_lead_time ?? undefined,
  ccLeadTimeDate: response.cc_lead_time_date || '',
  coo: response.coo || '',
  shipmentMode: response.shipment_mode || '',
  fnExclusive: response.fn_exclusive ?? undefined,
  fitSample: response.fit_sample ?? undefined,

  sampleSize: response.sample_size || '',
  colorWash: response.color_wash || '',
  typeOfDevelopment: response.type_of_development
    ? Object.values(DevelopmentType).find(
        (type) => type === response.type_of_development,
      )
    : undefined,
  juniorCost: response.junior_cost ?? undefined,
  juniorMoq: response.junior_moq ?? undefined,
  plusCost: response.plus_cost ?? undefined,
  plusMoq: response.plus_moq ?? undefined,
  poNumber: response.po_number || '',
  selectedProduct: response.product_sku || '',
  poLineName: response.po_line_name || '',
  poLineColor: response.po_line_color || '',
  poLineSize: response.po_line_size || '',
  comments: response.comments || '',
  fabricType: response.sample_created_with?.fabric,
  trimType: response.sample_created_with?.trim,
  labelType: response.sample_created_with?.label,
  washColorType: response.sample_created_with?.wash_color,
  fiberContents: response.self?.content?.map((fiber, index) => ({
    id: `fiber-${index}`,
    fiber: fiber.fiber,
    percent_of_fibers: fiber.percent_of_fibers,
  })) || [{ id: 'fiber-0', fiber: '', percent_of_fibers: 0 }],
  fabricWeight: response.self?.fabric_weight?.toString() || '',
  fabricCodeName: response.self?.fabric_code_name || '',
  productImageFile: undefined,
  contrastSection: transformMaterialToFabricSection(
    response.contrast,
    'contrast',
  ),
  liningSection: transformMaterialToFabricSection(response.lining, 'lining'),
  fillingSection: transformMaterialToFabricSection(response.filling, 'filling'),
  coatingSection: transformMaterialToFabricSection(response.coating, 'coating'),
  backingSection: transformMaterialToFabricSection(response.backing, 'backing'),
  blockNumber: response.block_number || '',
  pantsAttribute: response.pants_attribute || '',
  topsAttribute: response.tops_attribute || '',
  selfShellFabricCode: response.self_shell_fabric_code || '',
  id: response.id,
});
